import React, { useState, useEffect } from "react";
import { Form, Input, Button, Upload, message } from "antd";
import { Camera, LogOut, User, Mail, Phone, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import CryptoJS from "crypto-js";
import axios from "axios";
import { config } from "../../config/config";
const DefaultAvatar = "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%239ca3af'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/%3E%3C/svg%3E";

function Profile() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

  function decryptData(ciphertext) {
    if (!ciphertext) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    } catch {
      return null;
    }
  }

  function encryptData(data) {
    return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
  }

  let userId = null;
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const userObj = decryptData(userStr);
      userId = userObj?._id || userObj?.id;
    }
  } catch (error) {}

  useEffect(() => {
    // 1. Initial local storage load
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const userObj = decryptData(userStr);
      if (userObj) {
        form.setFieldsValue({
          firstName: userObj.firstName || "",
          lastName: userObj.lastName || "",
          phone: userObj.phone || "",
          email: userObj.email || "",
        });
        if (userObj.photo) {
          setImageUrl(userObj.photo);
        }
      }
    }

    // 2. Fetch from backend
    const fetchProfile = async () => {
      if (!userId) return;
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}${config.endpoints.userProfile}${userId}`,
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          }
        );
        const data = response.data;
        const profileData = data?.data || data; 
        if (profileData) {
          form.setFieldsValue({
            firstName: profileData.firstName || form.getFieldValue("firstName"),
            lastName: profileData.lastName || form.getFieldValue("lastName"),
            phone: profileData.phone || form.getFieldValue("phone"),
            email: profileData.email || form.getFieldValue("email"),
          });
          if (profileData.photo) {
            setImageUrl(profileData.photo);
          }
        }
      } catch (error) {
        
      }
    };
    
    fetchProfile();
  }, [form, userId]);

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp";
    if (!isJpgOrPng) {
      Swal.fire({
        icon: "error",
        title: "Invalid Format",
        text: "You can only upload JPG/PNG/WEBP files!",
        confirmButtonColor: "#ef4444",
      });
      return false;
    }
    const isGt50K = file.size / 1024 >= 50;
    if (!isGt50K) {
      Swal.fire({
        icon: "warning",
        title: "Image Too Small",
        text: "Image must be at least 50KB!",
        confirmButtonColor: "#4f46e5",
      });
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 <= 5;
    if (!isLt5M) {
      Swal.fire({
        icon: "warning",
        title: "Image Too Large",
        text: "Image must be smaller than 5MB!",
        confirmButtonColor: "#4f46e5",
      });
      return false;
    }
    
    // Read file and convert to base64 for local display/storage
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageUrl(e.target.result);
    };
    reader.readAsDataURL(file);
    return false; // Prevent default upload behavior
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        email: values.email,
        photo: imageUrl, // updated base64 image
      };

      if (userId) {
        await axios.put(
          `${config.apiBaseUrl}${config.endpoints.userProfile}${userId}`,
          payload,
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          }
        );
      }

      // Update local storage
      const userStr = localStorage.getItem("user");
      let userObj = decryptData(userStr) || {};
      
      userObj = {
        ...userObj,
        ...payload
      };

      localStorage.setItem("user", encryptData(userObj));
      
      // Dispatch event to update navbar immediately
      window.dispatchEvent(new Event("profileUpdate"));
      
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Profile updated successfully!",
        confirmButtonColor: "#4f46e5",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update profile.",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto overflow-x-hidden md:overflow-x-visible">
      {/* Premium Light Card Container */}
      <div className="relative overflow-hidden bg-white rounded-3xl p-6 md:p-8 text-slate-800 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 transition-all duration-500 hover:border-slate-200/80">
        
        {/* Card Header */}
        <div className="flex justify-between items-center mb-8 border-b border-slate-100 pb-5 relative z-10">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 text-slate-600">
              <User className="w-5 h-5 stroke-[2]" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-black tracking-tight text-slate-800 m-0">My Profile</h2>
              <p className="text-[10px] md:text-[11px] font-bold text-slate-400 tracking-wider uppercase mt-0.5">Manage Account Settings</p>
            </div>
          </div>
          
          <button 
            onClick={handleLogout}
            className="bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-100 px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 text-xs font-black tracking-wider uppercase shadow-sm cursor-pointer"
          >
            <LogOut className="w-4 h-4 stroke-[2]" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
        
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="relative z-10"
        >
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center lg:items-start mb-8">
            
            {/* Avatar Section */}
            <div className="flex flex-col items-center gap-4">
              <div className="group relative cursor-pointer">
                <Upload
                  name="avatar"
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                  accept="image/*"
                  className="rounded-full overflow-hidden"
                >
                  <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden bg-slate-50 border-4 border-white shadow-md flex items-center justify-center relative ring-4 ring-slate-100 group-hover:ring-slate-200 transition-all duration-300">
                    <img 
                      src={imageUrl || DefaultAvatar} 
                      alt="Avatar" 
                      className="w-full h-full object-cover" 
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-slate-900/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Camera className="text-white text-3xl mb-1.5 stroke-[2] animate-bounce" />
                      <span className="text-white text-[11px] font-black tracking-widest uppercase">Change Photo</span>
                    </div>
                  </div>
                </Upload>
              </div>
              <div className="text-center">
                <span className="text-[10px] font-black tracking-widest text-slate-400 uppercase">Profile Picture</span>
                <p className="text-[9px] text-slate-400 font-medium mt-1 max-w-[220px]">Allowed JPG, PNG or WEBP. Size between 50KB and 5MB.</p>
              </div>
            </div>
            
            {/* Fields Form Grid */}
            <div className="flex-1 w-full flex flex-col gap-y-5">
              
              {/* Row 1: Names (2 inputs per line) */}
              <div className="grid grid-cols-2 gap-3 md:gap-5">
                <div className="flex flex-col gap-1.5">
                  <span className="text-[9px] md:text-[11px] font-black tracking-[0.1em] md:tracking-[0.15em] text-slate-500 uppercase flex items-center gap-1 md:gap-1.5 truncate">
                    <User className="w-3 md:w-3.5 h-3 md:h-3.5 text-slate-400 stroke-[2.5] flex-shrink-0" />
                    First Name
                  </span>
                  <Form.Item
                    name="firstName"
                    rules={[{ required: true, message: 'Please enter your first name' }]}
                    className="mb-0"
                  >
                    <Input 
                      placeholder="First name" 
                      className="!bg-slate-50 !border-slate-200 hover:!border-slate-300 focus:!border-indigo-500 focus:!ring-1 focus:!ring-indigo-500/20 !text-slate-800 placeholder-slate-400 h-11 md:h-12 rounded-xl transition-all duration-300 text-xs md:text-sm font-semibold" 
                    />
                  </Form.Item>
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="text-[9px] md:text-[11px] font-black tracking-[0.1em] md:tracking-[0.15em] text-slate-500 uppercase flex items-center gap-1 md:gap-1.5 truncate">
                    <User className="w-3 md:w-3.5 h-3 md:h-3.5 text-slate-400 stroke-[2.5] flex-shrink-0" />
                    Last Name
                  </span>
                  <Form.Item
                    name="lastName"
                    rules={[{ required: true, message: 'Please enter your last name' }]}
                    className="mb-0"
                  >
                    <Input 
                      placeholder="Last name" 
                      className="!bg-slate-50 !border-slate-200 hover:!border-slate-300 focus:!border-indigo-500 focus:!ring-1 focus:!ring-indigo-500/20 !text-slate-800 placeholder-slate-400 h-11 md:h-12 rounded-xl transition-all duration-300 text-xs md:text-sm font-semibold" 
                    />
                  </Form.Item>
                </div>
              </div>

              {/* Row 2: Contact Details (2 inputs per line) */}
              <div className="grid grid-cols-2 gap-3 md:gap-5">
                <div className="flex flex-col gap-1.5">
                  <span className="text-[9px] md:text-[11px] font-black tracking-[0.1em] md:tracking-[0.15em] text-slate-500 uppercase flex items-center gap-1 md:gap-1.5 truncate">
                    <Phone className="w-3 md:w-3.5 h-3 md:h-3.5 text-slate-400 stroke-[2.5] flex-shrink-0" />
                    Phone
                  </span>
                  <Form.Item
                    name="phone"
                    rules={[{ required: true, message: 'Please enter your phone number' }]}
                    className="mb-0"
                  >
                    <Input 
                      placeholder="Phone number" 
                      className="!bg-slate-50 !border-slate-200 hover:!border-slate-300 focus:!border-indigo-500 focus:!ring-1 focus:!ring-indigo-500/20 !text-slate-800 placeholder-slate-400 h-11 md:h-12 rounded-xl transition-all duration-300 text-xs md:text-sm font-semibold" 
                    />
                  </Form.Item>
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="text-[9px] md:text-[11px] font-black tracking-[0.1em] md:tracking-[0.15em] text-slate-500 uppercase flex items-center gap-1 md:gap-1.5 truncate">
                    <Mail className="w-3 md:w-3.5 h-3 md:h-3.5 text-slate-400 stroke-[2.5] flex-shrink-0" />
                    Email
                  </span>
                  <Form.Item
                    name="email"
                    rules={[
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                    className="mb-0"
                  >
                    <Input 
                      placeholder="Email address" 
                      className="!bg-slate-50 !border-slate-200 hover:!border-slate-300 focus:!border-indigo-500 focus:!ring-1 focus:!ring-indigo-500/20 !text-slate-800 placeholder-slate-400 h-11 md:h-12 rounded-xl transition-all duration-300 text-xs md:text-sm font-semibold" 
                    />
                  </Form.Item>
                </div>
              </div>

            </div>
          </div>

          {/* Action Row */}
          <div className="flex justify-end pt-5 border-t border-slate-100">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-extrabold border-0 h-12 px-8 rounded-xl flex items-center justify-center gap-2 transition-all duration-300 shadow-[0_8px_25px_rgba(99,102,241,0.2)] hover:shadow-[0_8px_30px_rgba(99,102,241,0.35)] w-full sm:w-auto"
            >
              <Save className="w-4 h-4 stroke-[2.5]" />
              Save Profile Changes
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}

export default Profile;
